import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Table } from 'react-bootstrap';
import { RefreshCw, Check } from 'lucide-react';
import type { GoogleDriveFile } from '../../../types/drive';

interface DriveFileSyncModalProps {
    show: boolean;
    onHide: () => void;
    onSync: (fileIds: string[]) => Promise<void>;
    files: GoogleDriveFile[];
    isLoading: boolean;
    error: string | null;
    onRefresh: () => void;
    onLoadMore?: () => void;
    hasMore: boolean;
}

export const DriveFileSyncModal: React.FC<DriveFileSyncModalProps> = ({
                                                                          show,
                                                                          onHide,
                                                                          onSync,
                                                                          files,
                                                                          isLoading,
                                                                          error,
                                                                          onRefresh,
                                                                          onLoadMore,
                                                                          hasMore
                                                                      }) => {
    const [selectedFiles, setSelectedFiles] = useState<Set<string>>(() =>
        new Set(files.filter(file => file.isSynced).map(file => file.id))
    );
    const [searchQuery, setSearchQuery] = useState('');
    const [isSyncing, setIsSyncing] = useState(false);

    useEffect(() => {
        const syncedFileIds = new Set(files.filter(file => file.isSynced).map(file => file.id));
        setSelectedFiles(prevSelected => {
            const newSelected = new Set(prevSelected);
            syncedFileIds.forEach(id => newSelected.add(id));
            return newSelected;
        });
    }, [files]);

    const formatFileSize = (bytes: number | string): string => {
        const numBytes = typeof bytes === 'string' ? parseInt(bytes, 10) : bytes;

        if (isNaN(numBytes) || numBytes === 0) return '0 Bytes';

        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(numBytes) / Math.log(1024));

        return parseFloat((numBytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleSync = async () => {
        const newFilesToSync = Array.from(selectedFiles).filter(
            fileId => !files.find(f => f.id === fileId)?.isSynced
        );

        if (newFilesToSync.length === 0) {
            onHide();
            return;
        }

        setIsSyncing(true);
        try {
            await onSync(newFilesToSync);
            onHide();
        } finally {
            setIsSyncing(false);
        }
    };

    const filteredFiles = files.filter(file =>
        file.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        if (!hasMore || !onLoadMore || isLoading) return;

        const element = e.currentTarget;
        const isNearBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 100;

        if (isNearBottom) {
            onLoadMore();
        }
    };

    const handleToggleFile = (file: GoogleDriveFile) => {
        if (file.isSynced) return;

        setSelectedFiles(prevSelected => {
            const newSelected = new Set(prevSelected);
            if (newSelected.has(file.id)) {
                newSelected.delete(file.id);
            } else {
                newSelected.add(file.id);
            }
            return newSelected;
        });
    };

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Select Files from Google Drive</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {error && (
                    <Alert variant="danger" className="mb-3">
                        {error}
                    </Alert>
                )}

                <div className="mb-3 d-flex justify-content-between align-items-center">
                    <Form.Control
                        type="search"
                        placeholder="Search files..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="me-2"
                        style={{ maxWidth: '300px' }}
                    />
                    <Button
                        variant="outline-secondary"
                        onClick={() => void onRefresh()}
                        disabled={isLoading}
                        size="sm"
                    >
                        <RefreshCw size={16} className="me-2" />
                        Refresh
                    </Button>
                </div>

                <div
                    style={{ maxHeight: '400px', overflowY: 'auto' }}
                    onScroll={handleScroll}
                >
                    <Table hover>
                        <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                        <tr>
                            <th style={{ width: '40px' }}></th>
                            <th>File Name</th>
                            <th>Modified</th>
                            <th>Size</th>
                        </tr>
                        </thead>
                        <tbody>
                        {isLoading && filteredFiles.length === 0 ? (
                            <tr>
                                <td colSpan={4} className="text-center py-4">
                                    Loading files...
                                </td>
                            </tr>
                        ) : filteredFiles.length === 0 ? (
                            <tr>
                                <td colSpan={4} className="text-center py-4">
                                    No files found
                                </td>
                            </tr>
                        ) : (
                            filteredFiles.map(file => (
                                <tr
                                    key={file.id}
                                    onClick={() => handleToggleFile(file)}
                                    className={`cursor-pointer ${file.isSynced ? 'table-light' : ''}`}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <td className="text-center">
                                        {(selectedFiles.has(file.id) || file.isSynced) && (
                                            <Check
                                                size={16}
                                                className={file.isSynced ? "text-secondary" : "text-primary"}
                                            />
                                        )}
                                    </td>
                                    <td>
                                        {file.name}
                                        {file.isSynced && (
                                            <span className="ms-2 badge bg-secondary">Synced</span>
                                        )}
                                    </td>
                                    <td>{new Date(file.modifiedTime).toLocaleDateString()}</td>
                                    <td>{formatFileSize(file.size)}</td>
                                </tr>
                            ))
                        )}
                        {isLoading && filteredFiles.length > 0 && (
                            <tr>
                                <td colSpan={4} className="text-center py-2">
                                    Loading more files...
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </Table>
                </div>
            </Modal.Body>

            <Modal.Footer>
                <div className="text-muted me-auto">
                    {selectedFiles.size} file(s) selected
                </div>
                <Button variant="secondary" onClick={onHide}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={() => void handleSync()}
                    disabled={selectedFiles.size === 0 || isSyncing}
                >
                    {isSyncing ? 'Syncing...' : `Sync ${selectedFiles.size} Files`}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default DriveFileSyncModal;